import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Api } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';

const DEFAULT_COORDS = { lat: 33.8938, lng: 35.5018 };
const GOOGLE_MAPS_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_KEY ||
  process.env.GOOGLE_MAPS_KEY ||
  '';

let googleMapsScriptPromise = null;
const loadGoogleMapsScript = (apiKey) => {
  if (typeof window === 'undefined') {
    return Promise.reject(
      new Error('GOOGLE_MAPS_BROWSER_ONLY')
    );
  }
  if (window.google?.maps) {
    return Promise.resolve(window.google);
  }
  if (!apiKey) {
    return Promise.reject(new Error('GOOGLE_MAPS_MISSING_API_KEY'));
  }
  if (!googleMapsScriptPromise) {
    googleMapsScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.dataset.googleMaps = 'true';
      script.onload = () => resolve(window.google);
      script.onerror = (event) => {
        googleMapsScriptPromise = null;
        reject(event);
      };
      document.body.appendChild(script);
    });
  }
  return googleMapsScriptPromise;
};

const defaultFormState = {
  name: '',
  street: '',
  building: '',
  apartment: '',
  city: '',
  locationDescription: '',
};

const AddAddressModal = ({
  open,
  onClose,
  customerId,
  onCreated,
  address = null,
  onUpdated,
}) => {
  const [form, setForm] = useState(defaultFormState);
  const [coordinates, setCoordinates] = useState({
    lat: DEFAULT_COORDS.lat.toFixed(6),
    lng: DEFAULT_COORDS.lng.toFixed(6),
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [mapsError, setMapsError] = useState(null);
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const coordsRef = useRef(DEFAULT_COORDS);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const skipNextSearchRef = useRef(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const isEditing = Boolean(address?.addressId || address?.id);
  const { t } = useLanguage();

  const resolveMapsError = useCallback(
    (mapsError) => {
      const code = mapsError?.message;
      if (code === 'GOOGLE_MAPS_BROWSER_ONLY') {
        return t('add_address.browser_only');
      }
      if (code === 'GOOGLE_MAPS_MISSING_API_KEY') {
        return t('add_address.missing_api_key');
      }
      return t('add_address.maps_load_error');
    },
    [t]
  );

  const numericCoords = useMemo(
    () => ({
      lat: Number(coordinates.lat) || DEFAULT_COORDS.lat,
      lng: Number(coordinates.lng) || DEFAULT_COORDS.lng,
    }),
    [coordinates.lat, coordinates.lng]
  );

  useEffect(() => {
    coordsRef.current = numericCoords;
    if (markerRef.current) {
      markerRef.current.setPosition(numericCoords);
    }
    if (mapInstanceRef.current) {
      mapInstanceRef.current.panTo(numericCoords);
    }
  }, [numericCoords]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    setError(null);
    setMapsError(null);
    if (!GOOGLE_MAPS_KEY) {
      setMapsError(t('add_address.maps_key_missing'));
      return undefined;
    }

    let cancelled = false;
    loadGoogleMapsScript(GOOGLE_MAPS_KEY)
      .then((google) => {
        if (cancelled || !mapContainerRef.current) return;
        mapInstanceRef.current = new google.maps.Map(mapContainerRef.current, {
          center: coordsRef.current,
          zoom: 13,
          streetViewControl: false,
          mapTypeControl: false,
        });
        autocompleteServiceRef.current =
          new google.maps.places.AutocompleteService();
        placesServiceRef.current = new google.maps.places.PlacesService(
          mapInstanceRef.current
        );
        markerRef.current = new google.maps.Marker({
          position: coordsRef.current,
          map: mapInstanceRef.current,
          draggable: true,
        });
        markerRef.current.addListener('dragend', (event) => {
          if (!event?.latLng) return;
          const lat = event.latLng.lat();
          const lng = event.latLng.lng();
          setCoordinates({
            lat: lat.toFixed(6),
            lng: lng.toFixed(6),
          });
        });
        mapInstanceRef.current.addListener('click', (event) => {
          if (!event?.latLng) return;
          const lat = event.latLng.lat();
          const lng = event.latLng.lng();
          setCoordinates({
            lat: lat.toFixed(6),
            lng: lng.toFixed(6),
          });
        });
      })
      .catch((mapsLoadError) => {
        if (!cancelled) {
          setMapsError(resolveMapsError(mapsLoadError));
        }
      });

    return () => {
      cancelled = true;
      mapInstanceRef.current = null;
      markerRef.current = null;
      autocompleteServiceRef.current = null;
      placesServiceRef.current = null;
    };
  }, [open, resolveMapsError, t]);

useEffect(() => {
  if (!open) {
    setForm(defaultFormState);
    setCoordinates({
      lat: DEFAULT_COORDS.lat.toFixed(6),
      lng: DEFAULT_COORDS.lng.toFixed(6),
    });
    setSubmitting(false);
    setError(null);
    setMapsError(null);
    setSearchQuery('');
    setSearchResults([]);
    setSearching(false);
  }
}, [open]);

useEffect(() => {
  if (!open) {
    return;
  }
  if (address) {
    setForm({
      name: address.name || '',
      street: address.street || '',
      building: address.building || '',
      apartment: address.apartment || '',
      city: address.city || '',
      locationDescription:
        address.location_description ||
        address.locationDescription ||
        address.building ||
        '',
    });
    const lat =
      address.google_map_coordinates?.x ??
      address.latitude ??
      address.lat ??
      DEFAULT_COORDS.lat;
    const lng =
      address.google_map_coordinates?.z ??
      address.longitude ??
      address.lng ??
      DEFAULT_COORDS.lng;
    setCoordinates({
      lat: Number(lat || DEFAULT_COORDS.lat).toFixed(6),
      lng: Number(lng || DEFAULT_COORDS.lng).toFixed(6),
    });
  } else {
    setForm(defaultFormState);
    setCoordinates({
      lat: DEFAULT_COORDS.lat.toFixed(6),
      lng: DEFAULT_COORDS.lng.toFixed(6),
    });
  }
}, [address, open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (address) {
      setForm({
        name: address.name || '',
        street: address.street || '',
        building: address.building || '',
        apartment: address.apartment || '',
        city: address.city || '',
        locationDescription:
          address.location_description ||
          address.locationDescription ||
          address.building ||
          '',
      });
      const lat =
        address.google_map_coordinates?.x ??
        address.latitude ??
        address.lat ??
        DEFAULT_COORDS.lat;
      const lng =
        address.google_map_coordinates?.z ??
        address.longitude ??
        address.lng ??
        DEFAULT_COORDS.lng;
      setCoordinates({
        lat: Number(lat || DEFAULT_COORDS.lat).toFixed(6),
        lng: Number(lng || DEFAULT_COORDS.lng).toFixed(6),
      });
    } else {
      setForm(defaultFormState);
      setCoordinates({
        lat: DEFAULT_COORDS.lat.toFixed(6),
        lng: DEFAULT_COORDS.lng.toFixed(6),
      });
    }
  }, [address, open]);

  useEffect(() => {
    if (!open) return undefined;
    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false;
      setSearchResults([]);
      setSearching(false);
      return undefined;
    }
    if (!searchQuery || searchQuery.trim().length < 3) {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      setSearchResults([]);
      setSearching(false);
      return undefined;
    }
    if (!autocompleteServiceRef.current || !window.google?.maps?.places) {
      return undefined;
    }
    setSearching(true);
    searchDebounceRef.current = setTimeout(() => {
      autocompleteServiceRef.current.getPlacePredictions(
        {
          input: searchQuery,
          componentRestrictions: { country: 'lb' },
        },
        (predictions = [], status) => {
          if (
            status !== window.google.maps.places.PlacesServiceStatus.OK &&
            status !== window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS
          ) {
            setSearchResults([]);
          } else {
            setSearchResults(predictions);
          }
          setSearching(false);
        }
      );
    }, 250);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchQuery, open]);

  if (!open) {
    return null;
  }

  const updateField = (field, value) => {
    setForm((previous) => ({ ...previous, [field]: value }));
  };

  const closeModal = () => {
    if (submitting) return;
    onClose?.();
  };

  const handleSelectSuggestion = (suggestion) => {
    skipNextSearchRef.current = true;
    setSearchQuery(suggestion?.description || '');
    setSearchResults([]);
    setSearching(false);
    if (!placesServiceRef.current || !window.google?.maps?.places) {
      return;
    }
    placesServiceRef.current.getDetails(
      {
        placeId: suggestion.place_id,
        fields: ['geometry', 'formatted_address'],
      },
      (place, status) => {
        if (
          status !== window.google.maps.places.PlacesServiceStatus.OK ||
          !place?.geometry?.location
        ) {
          return;
        }
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setCoordinates({
          lat: lat.toFixed(6),
          lng: lng.toFixed(6),
        });
        if (place.formatted_address) {
          setForm((previous) => ({
            ...previous,
            locationDescription:
              previous.locationDescription || place.formatted_address,
          }));
        }
      }
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!customerId) {
      setError(t('add_address.sign_in_required'));
      return;
    }
    if (!form.name || !form.city) {
      setError(t('add_address.required_fields'));
      return;
    }

    if (!coordinates.lat || !coordinates.lng) {
      setError(t('add_address.pin_required'));
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        ...form,
        customerId,
        latitude: numericCoords.lat,
        longitude: numericCoords.lng,
      };
      const response = isEditing
        ? await Api.updateAddress(address.addressId || address.id, payload)
        : await Api.addAddress(payload);
      const savedAddress =
        response?.data?.data || response?.data || response?.address || response;
      if (isEditing) {
        onUpdated?.(savedAddress);
      } else {
        onCreated?.(savedAddress);
      }
      onClose?.();
      setForm(defaultFormState);
    } catch (err) {
      setError(
        err?.message || t('add_address.save_error')
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="inquiry-modal">
      <div className="inquiry-modal__backdrop" onClick={closeModal} />
      <div className="inquiry-modal__panel add-address-modal">
        <header className="add-address-modal__header">
          <div>
            <p className="eyebrow">
              {isEditing ? t('add_address.edit_title') : t('add_address.title')}
            </p>
          </div>
          <button
            type="button"
            className="link-button"
            onClick={closeModal}
            disabled={submitting}
          >
            {t('add_address.close')}
          </button>
        </header>

        <form className="add-address-form" onSubmit={handleSubmit}>
          <div className="add-address-modal__grid">
            <label className="checkout-field">
              <span>{t('add_address.name_label')}</span>
              <input
                type="text"
                value={form.name}
                onChange={(event) => updateField('name', event.target.value)}
                placeholder={t('add_address.name_placeholder')}
                required
              />
            </label>
            <label className="checkout-field">
              <span>{t('add_address.street_label')}</span>
              <input
                type="text"
                value={form.street}
                onChange={(event) => updateField('street', event.target.value)}
                placeholder={t('add_address.street_placeholder')}
              />
            </label>
            <label className="checkout-field">
              <span>{t('add_address.building_label')}</span>
              <input
                type="text"
                value={form.building}
                onChange={(event) => updateField('building', event.target.value)}
                placeholder={t('add_address.building_placeholder')}
              />
            </label>
            <label className="checkout-field">
              <span>{t('add_address.apartment_label')}</span>
              <input
                type="text"
                value={form.apartment}
                onChange={(event) =>
                  updateField('apartment', event.target.value)
                }
                placeholder={t('add_address.apartment_placeholder')}
              />
            </label>
            <label className="checkout-field">
              <span>{t('add_address.city_label')}</span>
              <input
                type="text"
                value={form.city}
                onChange={(event) => updateField('city', event.target.value)}
                placeholder={t('add_address.city_placeholder')}
                required
              />
            </label>
            <label className="checkout-field">
              <span>{t('add_address.description_label')}</span>
              <textarea
                value={form.locationDescription}
                onChange={(event) =>
                  updateField('locationDescription', event.target.value)
                }
                placeholder={t('add_address.description_placeholder')}
              />
            </label>
          </div>

          <div className="add-address-map-wrapper">
            <p className="eyebrow">{t('add_address.pin_label')}</p>
            <div className="add-address-search">
              <input
                type="text"
                placeholder={t('add_address.search_placeholder')}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
              {searching && (
                <span className="add-address-search__hint">
                  {t('add_address.searching')}
                </span>
              )}
              {searchResults.length > 0 && (
                <div className="add-address-suggestions">
                  {searchResults.map((result) => (
                    <button
                      type="button"
                      key={result.place_id}
                      onClick={() => handleSelectSuggestion(result)}
                    >
                      <strong>{result.structured_formatting?.main_text}</strong>
                      <small>
                        {result.structured_formatting?.secondary_text ||
                          result.description}
                      </small>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="add-address-modal__map" ref={mapContainerRef}>
              {mapsError && (
                <p className="checkout-hint">{mapsError}</p>
              )}
            </div>
          
          </div>

          {error && <p className="checkout-error">{error}</p>}

          <div className="add-address-modal__actions">
            <button
              type="button"
              className="button button--ghost"
              onClick={closeModal}
              disabled={submitting}
            >
              {t('add_address.cancel')}
            </button>
            <button type="submit" className="button" disabled={submitting}>
              {submitting ? t('add_address.saving') : t('add_address.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAddressModal;
